import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
    APP_INITIALIZER,
    ApplicationConfig,
    importProvidersFrom,
    isDevMode,
    provideBrowserGlobalErrorListeners,
    provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';

import { MSAL_INSTANCE, MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { BrowserCacheLocation, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';

import { environment } from '@environments/environment';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TranslationsModule } from '@translations/translations.module';
import { routes } from './app.routes';
import { CAMPAIGNS_API_PROVIDER } from './features/campaigns/infrastructure/campaigns.provider';
import { IMPERSONATION_API_PROVIDER } from './features/impersonation/infrastructure/impersonation.provider';
import { CAMPAIGNS_OFFERS_API_PROVIDER } from './features/offers/infrastructure/campaigns-offers.provider';
import { CAMPAIGNS_SEGMENTS_API_PROVIDER } from './features/segments/infrastructure/campaigns-segments.provider';
import { CAMPAIGNS_TAGS_API_PROVIDER } from './features/segments/infrastructure/campaigns-tags.provider';
import { authInterceptor } from './infrastructure/interceptors/auth.interceptor';
import { mockRoutingInterceptor } from './infrastructure/interceptors/mock.interceptor';
import { AuthService } from './infrastructure/services/auth/auth.service';
import { APP_REDUCERS } from './infrastructure/store/app.state';

export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: environment.ENTRA_ID_CLIENT_ID,
            authority: 'https://login.microsoftonline.com/a9a8e375-fac1-4ec2-820a-cfb6eb5cf01b',
            redirectUri: environment.ENTRA_ID_REDIRECT,
            // postLogoutRedirectUri: environment.ENTRA_ID_REDIRECT,
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
            storeAuthStateInCookie: false,
        },
        // system: {
        //     loadFrameTimeout: 15000,
        //     iframeHashTimeout: 15000,
        //     windowHashTimeout: 60000,
        //     allowRedirectInIframe: false,
        // },
    });
}

export function initializeMsal(msalInstance: IPublicClientApplication, authService: AuthService) {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return async () => {
        await msalInstance.initialize();
        await msalInstance.handleRedirectPromise();

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
            msalInstance.setActiveAccount(accounts[0]);
            await authService.loadUserFromAccount();
        }
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideZonelessChangeDetection(),
        provideRouter(routes, withComponentInputBinding()),
        provideHttpClient(withFetch(), withInterceptors([mockRoutingInterceptor, authInterceptor])),
        CAMPAIGNS_OFFERS_API_PROVIDER,
        CAMPAIGNS_SEGMENTS_API_PROVIDER,
        CAMPAIGNS_API_PROVIDER,
        CAMPAIGNS_TAGS_API_PROVIDER,
        IMPERSONATION_API_PROVIDER,
        importProvidersFrom(TranslationsModule),
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeMsal,
            deps: [MSAL_INSTANCE, AuthService],
            multi: true,
        },
        MsalService,
        MsalBroadcastService,
        provideStore(APP_REDUCERS, {
            runtimeChecks: {
                // strictStateImmutability and strictActionImmutability are enabled by default
                strictStateSerializability: false,
                strictActionSerializability: false,
                strictActionWithinNgZone: false,
                strictActionTypeUniqueness: true,
                // if you want to change complexe objects and that we have. We need to disable these settings
                // change strictStateImmutability, strictActionImmutability
                strictStateImmutability: false,
                strictActionImmutability: false,
            },
        }),
        provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    ],
};
